import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Paper,
  Chip,
  Divider,
  Box,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
} from "@material-ui/core";
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Computer as ComputerIcon,
  ArrowForward as ArrowForwardIcon,
} from "@material-ui/icons";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: "#fff",
    padding: theme.spacing(2, 3),
  },
  dialogContent: {
    padding: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  infoCard: {
    padding: theme.spacing(2),
    borderRadius: 8,
    backgroundColor: theme.palette.mode === "dark" ? "#2d2d2d" : "#f8f9fa",
  },
  infoLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    marginBottom: theme.spacing(0.5),
  },
  infoValue: {
    fontSize: "0.95rem",
    fontWeight: 500,
  },
  actionChip: {
    fontWeight: "bold",
  },
  createChip: {
    backgroundColor: "#10B981",
    color: "#fff",
  },
  updateChip: {
    backgroundColor: "#F59E0B",
    color: "#fff",
  },
  deleteChip: {
    backgroundColor: "#EF4444",
    color: "#fff",
  },
  diffTable: {
    "& th": {
      fontWeight: 600,
      backgroundColor: theme.palette.mode === "dark" ? "#2d2d2d" : "#f5f5f5",
    },
  },
  oldValue: {
    backgroundColor: "rgba(239, 68, 68, 0.1)",
    padding: theme.spacing(0.5, 1),
    borderRadius: 4,
    color: "#DC2626",
    fontFamily: "monospace",
    fontSize: "0.85rem",
    wordBreak: "break-word",
    maxWidth: 200,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  newValue: {
    backgroundColor: "rgba(16, 185, 129, 0.1)",
    padding: theme.spacing(0.5, 1),
    borderRadius: 4,
    color: "#059669",
    fontFamily: "monospace",
    fontSize: "0.85rem",
    wordBreak: "break-word",
    maxWidth: 200,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  arrowIcon: {
    color: theme.palette.text.secondary,
    margin: theme.spacing(0, 1),
  },
  userAvatar: {
    width: 40,
    height: 40,
    marginRight: theme.spacing(1.5),
    backgroundColor: theme.palette.primary.main,
  },
  userInfo: {
    display: "flex",
    alignItems: "center",
  },
  metadataItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
  jsonViewer: {
    backgroundColor: theme.palette.mode === "dark" ? "#1e1e1e" : "#f5f5f5",
    padding: theme.spacing(2),
    borderRadius: 8,
    fontFamily: "monospace",
    fontSize: "0.85rem",
    maxHeight: 300,
    overflowY: "auto",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  noChanges: {
    textAlign: "center",
    color: theme.palette.text.secondary,
    padding: theme.spacing(2),
  },
}));

const AuditLogDetailModal = ({ open, onClose, log }) => {
  const classes = useStyles();

  if (!log) return null;

  const formatDate = (dateString) => {
    try {
      return format(parseISO(dateString), "dd 'de' MMMM 'de' yyyy 'às' HH:mm:ss", {
        locale: ptBR,
      });
    } catch {
      return dateString;
    }
  };

  const getActionChipClass = (action) => {
    switch (action) {
      case "CREATE":
        return `${classes.actionChip} ${classes.createChip}`;
      case "UPDATE":
        return `${classes.actionChip} ${classes.updateChip}`;
      case "DELETE":
        return `${classes.actionChip} ${classes.deleteChip}`;
      default:
        return classes.actionChip;
    }
  };

  const getActionLabel = (action) => {
    const labels = {
      CREATE: "Criação",
      UPDATE: "Atualização",
      DELETE: "Exclusão",
    };
    return labels[action] || action;
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return "—";
    if (typeof value === "object") {
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return String(value);
      }
    }
    if (typeof value === "boolean") return value ? "Sim" : "Não";
    return String(value);
  };

  const renderChangedFields = () => {
    if (!log.changedFields || log.changedFields.length === 0) {
      return (
        <Typography className={classes.noChanges}>
          Nenhuma alteração de campo registrada.
        </Typography>
      );
    }

    return (
      <Table size="small" className={classes.diffTable}>
        <TableHead>
          <TableRow>
            <TableCell>Campo</TableCell>
            <TableCell>Valor Anterior</TableCell>
            <TableCell width={40}></TableCell>
            <TableCell>Valor Novo</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {log.changedFields.map((change, index) => (
            <TableRow key={index}>
              <TableCell>
                <Typography variant="body2" style={{ fontWeight: 500 }}>
                  {change.field}
                </Typography>
              </TableCell>
              <TableCell>
                <div className={classes.oldValue}>{formatValue(change.oldValue)}</div>
              </TableCell>
              <TableCell align="center">
                <ArrowForwardIcon className={classes.arrowIcon} fontSize="small" />
              </TableCell>
              <TableCell>
                <div className={classes.newValue}>{formatValue(change.newValue)}</div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  const renderJsonData = (data, title) => {
    if (!data || Object.keys(data).length === 0) return null;

    return (
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>{title}</Typography>
        <Paper variant="outlined" className={classes.jsonViewer}>
          {JSON.stringify(data, null, 2)}
        </Paper>
      </div>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <div className={classes.dialogTitle}>
        <Typography variant="h6">Detalhes do Log de Auditoria</Typography>
        <IconButton onClick={onClose} style={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </div>

      <DialogContent className={classes.dialogContent}>
        {/* Header Info */}
        <div className={classes.section}>
          <div className={classes.infoGrid}>
            <Paper className={classes.infoCard} variant="outlined">
              <Typography className={classes.infoLabel}>Ação</Typography>
              <Chip
                label={getActionLabel(log.action)}
                className={getActionChipClass(log.action)}
                size="small"
              />
            </Paper>
            <Paper className={classes.infoCard} variant="outlined">
              <Typography className={classes.infoLabel}>Recurso</Typography>
              <Typography className={classes.infoValue}>
                {log.resourceType}
                {log.resourceId && (
                  <Typography variant="caption" display="block" color="textSecondary">
                    ID: {log.resourceId}
                  </Typography>
                )}
              </Typography>
            </Paper>
            <Paper className={classes.infoCard} variant="outlined">
              <Typography className={classes.infoLabel}>Nome do Recurso</Typography>
              <Typography className={classes.infoValue}>
                {log.resourceName || "—"}
              </Typography>
            </Paper>
          </div>
        </div>

        <Divider style={{ margin: "16px 0" }} />

        {/* User Info */}
        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>
            <PersonIcon fontSize="small" /> Usuário Responsável
          </Typography>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <div className={classes.userInfo}>
              <Avatar className={classes.userAvatar}>
                {getInitials(log.user?.name)}
              </Avatar>
              <div>
                <Typography variant="body1" style={{ fontWeight: 500 }}>
                  {log.user?.name || "Usuário Removido"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {log.user?.email}
                </Typography>
              </div>
            </div>
            {log.affectedUser && log.affectedUser.id !== log.user?.id && (
              <div style={{ textAlign: "right" }}>
                <Typography variant="caption" color="textSecondary">
                  Usuário Afetado:
                </Typography>
                <Typography variant="body2">
                  {log.affectedUser.name} ({log.affectedUser.email})
                </Typography>
              </div>
            )}
          </Box>
        </div>

        <Divider style={{ margin: "16px 0" }} />

        {/* Description */}
        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>Descrição</Typography>
          <Typography variant="body1">{log.description}</Typography>
        </div>

        {/* Changed Fields (for UPDATE) */}
        {log.action === "UPDATE" && (
          <>
            <Divider style={{ margin: "16px 0" }} />
            <div className={classes.section}>
              <Typography className={classes.sectionTitle}>Campos Alterados</Typography>
              {renderChangedFields()}
            </div>
          </>
        )}

        {/* Full Data for CREATE or DELETE */}
        {log.action === "CREATE" && renderJsonData(log.newValues, "Dados Criados")}
        {log.action === "DELETE" && renderJsonData(log.oldValues, "Dados Removidos")}

        <Divider style={{ margin: "16px 0" }} />

        {/* Metadata */}
        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>Metadados</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <div className={classes.metadataItem}>
                <ScheduleIcon fontSize="small" />
                <span>{formatDate(log.createdAt)}</span>
              </div>
            </Grid>
            {log.ipAddress && (
              <Grid item xs={12} sm={6}>
                <div className={classes.metadataItem}>
                  <ComputerIcon fontSize="small" />
                  <span>IP: {log.ipAddress}</span>
                </div>
              </Grid>
            )}
          </Grid>
          {log.userAgent && (
            <Typography variant="caption" color="textSecondary" display="block">
              User Agent: {log.userAgent}
            </Typography>
          )}
        </div>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AuditLogDetailModal;
